import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export type AdminActionType = 
  // Auth Actions
  | 'login'
  | 'logout'
  | 'login_failed'
  | 'session_start'
  // Navigation Actions
  | 'view_page'
  | 'click_button'
  | 'click_link'
  | 'time_on_page'
  // User Management
  | 'view_users'
  | 'view_user'
  | 'create_user'
  | 'update_user'
  | 'delete_user'
  | 'update_permissions'
  // VDR Management
  | 'view_vdr'
  | 'view_vdr_logs'
  | 'create_vdr'
  | 'update_vdr'
  | 'delete_vdr'
  | 'create_link'
  | 'update_link'
  | 'delete_link'
  | 'create_page'
  | 'update_page'
  | 'delete_page'
  // Settings
  | 'update_settings'
  | 'update_profile'
  | 'change_password';

interface TrackerOptions {
  enabled?: boolean;
}

// Get or create session ID
const getSessionId = () => {
  if (typeof window === 'undefined') return null;
  
  let sessionId = sessionStorage.getItem('admin_session_id');
  if (!sessionId) {
    sessionId = generateUUID();
    sessionStorage.setItem('admin_session_id', sessionId);
  }
  return sessionId;
};

// Get device info
const getDeviceInfo = () => {
  if (typeof window === 'undefined') return {};
  
  return {
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    language: navigator.language,
    platform: navigator.platform,
    color_depth: window.screen.colorDepth,
    pixel_ratio: window.devicePixelRatio,
    user_agent: navigator.userAgent,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

// Get current user info from localStorage
const getUserInfo = () => {
  if (typeof window === 'undefined') return { userId: null, userEmail: null, userName: null };
  
  return {
    userId: localStorage.getItem('app_user_id'),
    userEmail: localStorage.getItem('app_email'),
    userName: localStorage.getItem('app_name'),
  };
};

export function useAdminTracker(options: TrackerOptions = {}) {
  const { enabled = true } = options;
  const location = useLocation();
  const pathRef = useRef<string>(location.pathname);
  const maxScrollRef = useRef<number>(0);
  const startTimeRef = useRef<number>(Date.now());

  // Track function - main tracking method
  const track = useCallback(async (
    actionType: AdminActionType, 
    metadata: Record<string, any> = {},
    targetInfo?: { targetId?: string; targetType?: string }
  ) => {
    if (!enabled) return;
    
    try {
      const { userId, userEmail, userName } = getUserInfo();
      const deviceInfo = getDeviceInfo();
      const sessionId = getSessionId();

      const body = {
        userId,
        userEmail,
        userName,
        actionType,
        actionCategory: getCategoryForAction(actionType),
        metadata,
        path: window.location.pathname,
        targetId: targetInfo?.targetId,
        targetType: targetInfo?.targetType,
        sessionId,
        deviceInfo,
      };

      // Use beacon for time_on_page on unload for reliability
      if (actionType === 'time_on_page' && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
        navigator.sendBeacon(`${API_URL}/admin-logs`, blob);
      } else {
        await fetch(`${API_URL}/admin-logs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          keepalive: true,
        });
      }
    } catch (err) {
      console.error('Failed to track admin event', err);
    }
  }, [enabled]);

  // Track clicks globally
  useEffect(() => {
    if (!enabled) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const interactive = target.closest('button, a, [role="button"], input[type="submit"], input[type="button"]') as HTMLElement | null;
      
      if (interactive) {
        const tagName = interactive.tagName.toLowerCase();
        let label = interactive.innerText || interactive.getAttribute('aria-label') || interactive.getAttribute('title') || '';
        if (label.length > 100) label = label.substring(0, 100) + '...';
        
        const metadata: any = {
          tagName,
          id: interactive.id,
          className: interactive.className,
          label: label.trim(),
          text: interactive.innerText?.substring(0, 50).trim(),
        };

        if (tagName === 'a') {
          metadata.href = (interactive as HTMLAnchorElement).href;
          track('click_link', metadata);
        } else {
          track('click_button', metadata);
        }
      }
    };

    window.addEventListener('click', handleClick, { capture: true, passive: true });
    return () => window.removeEventListener('click', handleClick, { capture: true } as any);
  }, [track, enabled]);

  // Track scroll depth
  useEffect(() => {
    if (!enabled) return;

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) return;
      const scrollTop = window.scrollY;
      const percent = Math.min(100, Math.max(0, Math.round((scrollTop / scrollHeight) * 100)));
      
      if (percent > maxScrollRef.current) {
        maxScrollRef.current = percent;
      }
    };
    
    maxScrollRef.current = 0;
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname, enabled]);

  // Track page views and time on page
  useEffect(() => {
    if (!enabled) return;

    // Track page view
    track('view_page', {
      referrer: document.referrer,
      title: document.title,
      search: location.search,
    });

    startTimeRef.current = Date.now();
    pathRef.current = location.pathname;

    return () => {
      // Track time on page when leaving
      const duration = Date.now() - startTimeRef.current;
      const leavingPath = pathRef.current;
      const { userId, userEmail, userName } = getUserInfo();
      const deviceInfo = getDeviceInfo();
      const sessionId = getSessionId();

      const body = {
        userId,
        userEmail,
        userName,
        actionType: 'time_on_page',
        actionCategory: 'navigation',
        metadata: { 
          duration_ms: duration, 
          scroll_depth: maxScrollRef.current 
        },
        path: leavingPath,
        sessionId,
        deviceInfo,
      };
      
      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
        navigator.sendBeacon(`${API_URL}/admin-logs`, blob);
      } else {
        fetch(`${API_URL}/admin-logs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          keepalive: true,
        }).catch(e => console.error(e));
      }
    };
  }, [location.pathname, location.search, enabled]);

  return { track };
}

// Helper to categorize actions
function getCategoryForAction(actionType: AdminActionType): string {
  const categories: Record<string, AdminActionType[]> = {
    auth: ['login', 'logout', 'login_failed', 'session_start'],
    users: ['view_users', 'view_user', 'create_user', 'update_user', 'delete_user', 'update_permissions'],
    vdr: ['view_vdr', 'view_vdr_logs', 'create_vdr', 'update_vdr', 'delete_vdr', 'create_link', 'update_link', 'delete_link', 'create_page', 'update_page', 'delete_page'],
    settings: ['update_settings', 'update_profile', 'change_password'],
    navigation: ['view_page', 'click_button', 'click_link', 'time_on_page'],
  };

  for (const [category, types] of Object.entries(categories)) {
    if (types.includes(actionType)) {
      return category;
    }
  }
  return 'other';
}

// Standalone track function for use outside React components (e.g., login)
export async function trackAdminAction(
  actionType: AdminActionType,
  metadata: Record<string, any> = {},
  targetInfo?: { targetId?: string; targetType?: string }
) {
  try {
    const { userId, userEmail, userName } = getUserInfo();
    const deviceInfo = getDeviceInfo();
    const sessionId = getSessionId();

    await fetch(`${API_URL}/admin-logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        userEmail,
        userName,
        actionType,
        actionCategory: getCategoryForAction(actionType),
        metadata,
        path: window.location.pathname,
        targetId: targetInfo?.targetId,
        targetType: targetInfo?.targetType,
        sessionId,
        deviceInfo,
      }),
    });
  } catch (err) {
    console.error('Failed to track admin event', err);
  }
}
